import React, { useCallback, useEffect, useState } from 'react';
import API from '../../services/api';
import Icon from '../../components/ui/Icon';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import { Skeleton } from '../../components/ui/Skeleton';
import { usePopup } from '../../context/PopupContext';
import { timeAgo } from '../../utils/time';
import { CATEGORIES } from '../Feedback';
import './FeedbackInbox.css';

const TABS = [
  { key: 'new', label: 'New' },
  { key: 'in_review', label: 'In review' },
  { key: 'resolved', label: 'Resolved' },
];
const ROLE_LABEL = { student: 'Student', college_admin: 'College admin', super_admin: 'Super admin' };
const NEXT = {
  new: [{ to: 'in_review', label: 'Start review', icon: 'eye' }, { to: 'resolved', label: 'Resolve', icon: 'check' }],
  in_review: [{ to: 'resolved', label: 'Resolve', icon: 'check' }, { to: 'new', label: 'Back to new', icon: 'refresh', secondary: true }],
  resolved: [{ to: 'new', label: 'Reopen', icon: 'refresh', secondary: true }],
};

// Zaptude product-team triage (super_admin only). Everything here is real data from GET /api/feedback; the internal
// note is written and read here and never returned to the person who sent the feedback.
export default function FeedbackInbox() {
  const { showToast } = usePopup();
  const [status, setStatus] = useState('new');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await API.get('/feedback', { params: { status, category: category || undefined, page, limit: 20 } });
      setData(res.data);
    } catch (err) { setError(err); }
  }, [status, category, page]);
  useEffect(() => { setData(null); load(); }, [load]);

  const changeTab = (k) => { setStatus(k); setPage(1); setOpenId(null); };
  const toggle = (item) => { setOpenId(id => (id === item.id ? null : item.id)); setNote(item.internal_note || ''); };

  const update = async (item, body, done) => {
    setBusy(true);
    try {
      await API.patch(`/feedback/${item.id}`, body);
      showToast(done, { tone: 'success' });
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not update that item.', { tone: 'error' });
    } finally { setBusy(false); }
  };

  const items = data?.items || [];
  const pages = data?.metadata?.total_pages || 1;

  return (
    <div className="z-page fbi-page">
      <div className="fbi-head">
        <div>
          <h1 className="fbi-title">Feedback inbox</h1>
          <p className="fbi-sub">What students and admins have told the Zaptude team.</p>
        </div>
      </div>

      <div className="fbi-controls">
        <div className="z-segments" role="tablist" aria-label="Feedback status">
          {TABS.map(t => (
            <button key={t.key} type="button" role="tab" className="z-segment" aria-selected={status === t.key} onClick={() => changeTab(t.key)}>
              {t.label}<span className="z-chip fbi-count">{data ? data.counts[t.key] : '–'}</span>
            </button>
          ))}
        </div>
        <label className="fbi-filter">
          <span className="z-sr-only">Category</span>
          <select className="z-select" value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : data === null ? (
        <div className="fbi-list" role="status" aria-label="Loading feedback">{[0, 1, 2].map(i => <Skeleton key={i} height={120} radius={16} />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon="inbox" art={false} tone="neutral" title={status === 'new' ? 'Inbox zero' : 'Nothing here'} body={status === 'new' ? 'No new feedback right now.' : `No ${TABS.find(t => t.key === status).label.toLowerCase()} feedback${category ? ' in this category' : ''}.`} />
      ) : (
        <div className="fbi-list">
          {items.map(item => {
            const cat = CATEGORIES.find(c => c.key === item.category);
            const open = openId === item.id;
            return (
              <article key={item.id} className={`z-surface fbi-item${open ? ' fbi-item--open' : ''}`}>
                <div className="fbi-item-top">
                  <span className={`z-chip fbi-cat fbi-cat--${item.category}`}><Icon name={cat?.icon || 'feedback'} size={14} /> {cat?.label}</span>
                  <span className="fbi-when">{timeAgo(item.created_at)}</span>
                </div>
                <p className={`fbi-msg${open ? ' fbi-msg--open' : ''}`}>{item.message}</p>
                <div className="fbi-who">
                  <strong>{item.user_name}</strong>
                  <span className="z-chip">{ROLE_LABEL[item.role] || item.role}</span>
                  <span className="fbi-college">{item.college_name || 'Zaptude (no college)'}</span>
                  {item.page && <span className="fbi-page-ctx" title="Screen it was sent from">{item.page}</span>}
                </div>

                <div className="fbi-actions">
                  {NEXT[item.status].map(a => (
                    <button key={a.to} type="button" disabled={busy} className={`z-btn z-btn--sm ${a.secondary ? 'z-btn--secondary' : 'z-btn--primary'}`}
                      onClick={() => update(item, { status: a.to }, a.to === 'resolved' ? 'Marked resolved.' : a.to === 'in_review' ? 'Moved to review.' : 'Moved back to new.')}>
                      <Icon name={a.icon} size={16} /> {a.label}
                    </button>
                  ))}
                  <button type="button" className="z-btn z-btn--ghost z-btn--sm fbi-note-toggle" onClick={() => toggle(item)} aria-expanded={open}>
                    <Icon name="edit" size={16} /> {item.internal_note ? 'Edit note' : 'Add note'}
                  </button>
                </div>

                {item.internal_note && !open && <div className="fbi-note-preview"><Icon name="lock" size={14} /> {item.internal_note}</div>}
                {open && (
                  <div className="fbi-note">
                    <label className="z-label" htmlFor={`note-${item.id}`}><Icon name="lock" size={14} /> Internal note — only the Zaptude team can see this</label>
                    <textarea id={`note-${item.id}`} className="z-textarea" rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
                    <div className="fbi-note-actions">
                      <button type="button" className="z-btn z-btn--ghost z-btn--sm" onClick={() => setOpenId(null)}>Cancel</button>
                      <button type="button" className="z-btn z-btn--primary z-btn--sm" disabled={busy} onClick={() => update(item, { internal_note: note.trim() || null }, 'Note saved.').then(() => setOpenId(null))}>Save note</button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <div className="fbi-paging">
          <button type="button" className="z-btn z-btn--secondary z-btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icon name="chevron-left" size={16} /> Previous</button>
          <span className="fbi-page-info">Page {page} of {pages}</span>
          <button type="button" className="z-btn z-btn--secondary z-btn--sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next <Icon name="chevron-right" size={16} /></button>
        </div>
      )}
    </div>
  );
}
